import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReadConfigService } from '../common/read-config.service';
import { Global } from '../../global.constants';
import { timeout, catchError, map } from 'rxjs/operators';
import { DisplayNotificationService } from '../common/notification.service';

@Injectable({
  providedIn: 'root'
})
export class EmployeesService {

  constructor(
    private http: HttpClient,
    private readConfigService: ReadConfigService,
    private notificationService: DisplayNotificationService) { }

  readonly rootUrl = this.readConfigService.getApiUrl();
  apiTimeout = this.readConfigService.getApiTimeout();
  reqHeader = new HttpHeaders({ 'Content-Type': 'application/json' });
  errorMessage = this.readConfigService.getDocumentErrorMessage();
  errorSize = this.readConfigService.getDocumentMinSize();

  getEmployeeInfo(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_EMPLOYEE_INFO_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getEnrollmentSalaryData(data: any) {
    const url = this.rootUrl + Global.BASE_GET_ENROLLMENT_SALARY_INFO_ENDPOINT;
    return this.http.post<any>(url, data)
      .pipe(this.handleError());
  }

  getEmployerList(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ORG_LIST_DATA_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  postTerEmpData(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_POST_TERMINATE_EMP_DATA_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  updateTempTask(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_POST_UPDATE_TERMINATE_TASK_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getCutoffNoticesData(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_CUTOFF_NOTICES_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getEmploymentInfo(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ENROLLMENT_INFO_BY_SSN_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getPersonInfo(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ENROLLMENT_PERSON_INFO_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getEnrollmentInput(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ENROLLMENT_LOC_AND_SYS_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getEnrollmentPayschedule(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ENROLLMENT_PAY_SCHEDULE_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getPendingEnrollData(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_PENDING_ENROLLMENTS_HISTORY_DATA_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  updateEnrollmentRecordOwner(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_UPDATE_ENROLLMENT_RECORD_OWNER_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  deletePendingEnrollRecord(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_PENDING_ENROLL_RECORD_DELETE_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  savePersonInfo(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_SAVE_PERSON_INFO_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  saveEnrollmentInfo(data: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('userInfo', JSON.stringify(data.userInfo));
    formData.append('enrollmentInfo', JSON.stringify(data.enrollmentInfo));
    formData.append('uploadFile', data.uploadedFile?.rawFile);
    formData.append('fileName', data.fileName);
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    const url = this.rootUrl + Global.BASE_SAVE_SCHEDULE_INFO_ENDPOINT;
    return this.http.post<any>(url, formData, { headers })
      .pipe(this.handleError());
  }

  saveContactInfo(data: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('userInfo', JSON.stringify(data.userInfo));
    formData.append('contactInfo', JSON.stringify(data.contactInfo));
    formData.append('uploadFile', data.uploadedFile?.rawFile);
    formData.append('fileName', data.fileName);
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    const url = this.rootUrl + Global.BASE_SAVE_CONTACT_INFO_ENDPOINT;
    return this.http.post<any>(url, formData, { headers })
      .pipe(this.handleError());
  }

  downloadUploadedFile(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_DOWNLOAD_UPLOADED_FILE_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader, responseType: 'blob' as 'json' })
      .pipe(this.handleErrorWithDocSize(data.fileName));
  }

  downloadFile(response: any, filename: string): string {
    const blob = new Blob([response]);
    const url = window.URL.createObjectURL(blob); // <-- work with blob directly
    // IE & Edge
    const nav = (window.navigator as any);
    if (nav && nav.msSaveOrOpenBlob) {
      // msSaveBlob only available for IE & Edge
      nav.msSaveOrOpenBlob(blob, filename);
    } else {
      // create hidden dom element (so it works in all browsers)
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(url);
      }, 100);
    }
    return url;
  }

  handleError() {
    return timeout(this.apiTimeout),
      catchError((err: any) => {
        return throwError(err || 'Server error');
      }),
      map((v: any) => {
        if (v) {
          return v;
        }
      });
  }

  handleErrorWithDocSize(fileName: string) {
    return timeout(this.apiTimeout),
      catchError((err: any) => {
        return throwError(err || 'Server error');
      }),
      map((res: any) => {
        if (res && res.size > this.errorSize) {
          this.downloadFile(res, fileName);
        } else {
          this.notificationService.setErrorNotification(this.errorMessage);
        }
      });
  }
}
